<script setup>
import { onMounted, ref, computed, watch, nextTick, defineExpose, defineEmits } from 'vue';

import MapView from '../../../experimental/hybrid-view/MapView.vue';

import Forms from '../forms/Forms.vue';
import { currentModel } from '../forms/currentModel';

import { Notify } from 'quasar';

// Declare emits
const emit = defineEmits(['request-delete']);

const props = defineProps({
  customerList: Array,
  driverList: Array,
  isUpdateMode: {
    type: Boolean,
    default: false
  },
  bookingStatus: {
    type: String,
    default: null
  },
  canEditFields: {
    type: Boolean,
    default: true
  }
});

// Computed property for date/time validation (Revised Timestamp Comparison)
const isDateTimeValid = computed(() => {
  const temp = currentModel.value && currentModel.value.temp;
  if (!temp || !temp.date || !temp.time) {
    return true; // Don't validate if fields aren't filled
  }

  console.log(`%cValidating Date: ${temp.date}, Time: ${temp.time}`, 'color: blue; font-weight: bold;');

  try {
    const now = new Date();

    // Parse date parts (handle both YYYY-MM-DD and YYYY/MM/DD)
    const dateStr = temp.date;
    const normalizedDateStr = dateStr.replace(/\//g, '-'); // Replace slashes with hyphens
    const dateParts = normalizedDateStr.split('-'); // Now split by hyphen
    
    if (dateParts.length !== 3) {
      console.error("[Validation] Invalid date parts after normalization:", dateParts, "(Original: ", dateStr, ")");
      return false;
    }
    const year = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10) - 1; // JS months 0-11
    const day = parseInt(dateParts[2], 10);

    // Parse time parts (assuming HH:mm)
    const timeParts = temp.time.split(':');
    if (timeParts.length < 2) {
      console.error("[Validation] Invalid time parts:", timeParts);
      return false;
    }
    const hour = parseInt(timeParts[0], 10);
    const minute = parseInt(timeParts[1], 10);

    // Sanity checks
    if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(hour) || isNaN(minute)) {
      console.error("[Validation] NaN detected in parsed date/time components");
      return false;
    }
    if (month < 0 || month > 11 || day < 1 || day > 31 || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
      console.error("[Validation] Invalid range for date/time components");
      return false;
    }

    // Construct the selected date object
    const selectedDate = new Date(year, month, day, hour, minute, 0, 0);

    if (isNaN(selectedDate.getTime())) {
      console.error("[Validation] Constructed Date object is invalid (NaN)");
      return false;
    }

    // Get timestamps
    const nowTs = now.getTime();
    const selectedTs = selectedDate.getTime();

    console.log(`[Validation] Current Timestamp: ${nowTs} (${now.toISOString()})`);
    console.log(`[Validation] Selected Timestamp: ${selectedTs} (${selectedDate.toISOString()})`);

    // Compare timestamps
    const isFuture = selectedTs > nowTs;
    console.log(`%c[Validation] Is selectedTs > nowTs? ${isFuture}`, isFuture ? 'color: green' : 'color: red');

    return isFuture;

  } catch (error) {
    console.error("[Validation] Error during date/time validation:", error);
    return false;
  }
});

// Initialize currentModel.temp if it doesn't exist
onMounted(() => {
  if (!currentModel.value) {
    currentModel.value = {};
  }
  if (!currentModel.value.trips) {
    currentModel.value.trips = [];
  }
  if (!currentModel.value.temp) {
    currentModel.value.temp = {};
  }
});

const dialog = ref(false);

function addTrip() {
  console.log("Add Trip called with temp data:", currentModel.value.temp);

  // *** Add check for date/time validity ***
  if (!isDateTimeValid.value) {
      Notify.create({
          message: 'Booking date and time must be in the future.',
          classes: 'bg-red px-4',
      });
      return;
  }

  // Basic validation (already partially handled by button disable state)
  if (!currentModel.value || !currentModel.value.temp || !currentModel.value.temp.tripType) {
    Notify.create({ message: 'Trip type is required', classes: 'bg-red px-4' });
    return;
  }
  if (!currentModel.value.temp.date || !currentModel.value.temp.time) {
    Notify.create({ message: 'Date and time are required', classes: 'bg-red px-4' });
    return;
  }

  const tripType = currentModel.value.temp.tripType;
  const pickupPoi = currentModel.value.temp.pickupPoi;
  const dropoffPoi = currentModel.value.temp.dropoffPoi;
  const customer = currentModel.value.temp.customer;

  // Validate required locations based on trip type
  if (tripType === 'pickup' && (!pickupPoi || !dropoffPoi)) {
    Notify.create({ message: 'Both pickup and dropoff locations are required for a pickup trip', classes: 'bg-red px-4' });
    return;
  }
  if (tripType === 'pickup' && pickupPoi.id === dropoffPoi.id) {
    Notify.create({ message: 'Pickup and dropoff locations cannot be the same.', classes: 'bg-red px-4' });
    return;
  }
  if (tripType === 'dropoff' && !dropoffPoi) {
    Notify.create({ message: 'Dropoff location is required for a dropoff trip', classes: 'bg-red px-4' });
    return;
  }
  if (!customer) {
     Notify.create({ message: 'Customer data is missing', classes: 'bg-red px-4' });
     return;
  }

  // Create the base trip object
  const obj = {
    customerId: customer.id,
    clientId: customer.clientId, // Assuming clientId is on customer object
    tripType: tripType,
    estimatedJobStartDate: currentModel.value.temp.date,
    estimatedJobStartTime: currentModel.value.temp.time,
    status: 'unassigned', // Default status
    meta: { // Populate meta object with POI details
      customer: { // Store minimal customer info needed for display/quick access
         id: customer.id,
         companyName: customer.companyName
      },
      // Initialize POI fields that will be populated based on trip type
      poi: null,
      address: null,
      coordinate: null,
      dropoffPoi: null,
      dropoffAddress: null
    },
    locations: [] // Initialize locations array (will contain simplified objects)
  };

  // Add driver if selected
  if (currentModel.value.temp.driver) {
    obj.driverId = currentModel.value.temp.driver.id;
    obj.status = 'assigned';
    obj.meta.driver = { // Store minimal driver info in meta
      id: currentModel.value.temp.driver.id,
      name: currentModel.value.temp.driver.name
    };
    obj.meta.driverName = currentModel.value.temp.driver.name; // Keep redundant name if needed
  }

  // Populate meta and locations based on trip type
  if (tripType === 'pickup') {
    // Populate meta for pickup
    obj.meta.poi = { id: pickupPoi.id, name: pickupPoi.name }; // Minimal POI info in meta
    obj.meta.address = currentModel.value.temp.pickupPoiAddress || pickupPoi.address;
    obj.meta.coordinate = `${pickupPoi.centerLat || ''} , ${pickupPoi.centerLng || ''}`; // Add coordinate to meta
    // Populate meta for dropoff
    obj.meta.dropoffPoi = { id: dropoffPoi.id, name: dropoffPoi.name };
    obj.meta.dropoffAddress = currentModel.value.temp.dropoffPoiAddress || dropoffPoi.address;
    // Add coordinate for dropoffPoi if available (assuming dropoffPoi has centerLat/Lng)
    // obj.meta.dropoffCoordinate = `${dropoffPoi.centerLat || ''} , ${dropoffPoi.centerLng || ''}`; 

    // Add simplified locations to array
    obj.locations.push({
      poiId: pickupPoi.id,
      locationType: 'pickup',
      primary: true,
      sequence: 1
      // REMOVED meta from here
    });
    obj.locations.push({
      poiId: dropoffPoi.id,
      locationType: 'dropoff',
      primary: false,
      sequence: 2
      // REMOVED meta from here
    });
  } else if (tripType === 'dropoff') {
    // Populate meta for dropoff
    obj.meta.poi = { id: dropoffPoi.id, name: dropoffPoi.name };
    obj.meta.address = currentModel.value.temp.dropoffPoiAddress || dropoffPoi.address;
    obj.meta.coordinate = `${dropoffPoi.centerLat || ''} , ${dropoffPoi.centerLng || ''}`; // Add coordinate to meta

    // Add simplified location to array
    obj.locations.push({
      poiId: dropoffPoi.id,
      locationType: 'dropoff',
      primary: true,
      sequence: 1
      // REMOVED meta from here
    });
  }

  // Add or update trip in the main list
  if (!currentModel.value.trips) {
    currentModel.value.trips = [];
  }

  if (editIndex.value === undefined) {
    currentModel.value.trips.push(obj);
    Notify.create({ message: 'Trip added successfully', classes: 'bg-green px-4' });
  } else {
    currentModel.value.trips.splice(editIndex.value, 1, obj);
    editIndex.value = undefined;
    Notify.create({ message: 'Trip updated successfully', classes: 'bg-green px-4' });
  }

  // Reset temp object and close dialog
  currentModel.value.temp = {};
  dialog.value = false;
}

function edit(index, trip) {
  console.log("Editing trip:", index, trip);
  if (index != undefined) {
    editIndex.value = index;

    // Pre-populate temp object with data from the trip being edited
    // Reset temp first to avoid merging issues
    currentModel.value.temp = {}; 

    // Map relevant fields carefully
    currentModel.value.temp.tripType = trip.tripType;
    currentModel.value.temp.date = trip.estimatedJobStartDate;
    currentModel.value.temp.time = trip.estimatedJobStartTime;

    // Assign driver if exists
    if (trip.driverId) {
      // Find the full driver object from the list if possible
      const foundDriver = props.driverList.find(d => d.id === trip.driverId);
      currentModel.value.temp.driver = foundDriver || (trip.meta && trip.meta.driver); // Use full object or fallback to meta
    } else {
      currentModel.value.temp.driver = null;
    }

    // Extract primary pickup and dropoff locations (if they exist)
    const primaryPickup = (trip.locations && trip.locations.find(loc => loc.locationType === 'pickup' && loc.primary));
    const primaryDropoff = (trip.locations && trip.locations.find(loc => loc.locationType === 'dropoff' && loc.primary));
    const secondaryDropoff = (trip.locations && trip.locations.find(loc => loc.locationType === 'dropoff' && !loc.primary)); // For pickup trips

    // Assign POIs based on trip type and locations array
    if (trip.tripType === 'pickup') {
      if (primaryPickup) {
        currentModel.value.temp.pickupPoi = { id: primaryPickup.poiId, name: (primaryPickup.meta && primaryPickup.meta.name), ...primaryPickup.meta }; // Simplified, might need full POI object
        currentModel.value.temp.pickupPoiAddress = (primaryPickup.meta && primaryPickup.meta.address);
      }
      if (secondaryDropoff) {
        currentModel.value.temp.dropoffPoi = { id: secondaryDropoff.poiId, name: (secondaryDropoff.meta && secondaryDropoff.meta.name), ...secondaryDropoff.meta };
        currentModel.value.temp.dropoffPoiAddress = (secondaryDropoff.meta && secondaryDropoff.meta.address);
      }
    } else if (trip.tripType === 'dropoff') {
      if (primaryDropoff) {
        currentModel.value.temp.dropoffPoi = { id: primaryDropoff.poiId, name: (primaryDropoff.meta && primaryDropoff.meta.name), ...primaryDropoff.meta };
        currentModel.value.temp.dropoffPoiAddress = (primaryDropoff.meta && primaryDropoff.meta.address);
      }
    }

    // Store customer (likely from trip.meta or could be passed differently)
    currentModel.value.temp.customer = (trip.meta && trip.meta.customer) || { id: trip.customerId }; // Fallback

    // Open the dialog
    dialog.value = true;
  }
}

const poiDialog = ref(false);
const dropoffPoiDialog = ref(false);

const form = ref([
  {
    is: 'Row',
    ui: [
      {
        is: 'TInput',
        Label: 'Customer',
        disable: true,
        model: 'temp.customerName',
      },
      {
        is: 'TSelect',
        Label: 'Trip Type',
        icon: 'keyboard_arrow_down',
        useInput: true,
        options: ['pickup', 'dropoff'],
        model: 'temp.tripType',
      },
    ],
  },
  {
    is: 'Row',
    ui: [
      {
        is: 'TDate',
        Label: 'Booking Date',
        icon: 'keyboard_arrow_down',
        model: 'temp.date',
      },
      {
        is: 'TTime',
        Label: 'Booking Time',
        format24h: true,
        model: 'temp.time',
      },
    ],
  }
]);

const customer = ref(null);
const selectedPoi = ref(null);
const dropoffPoiId = ref(null);

// *** Ensure selectedPoiType ref is defined ***
const selectedPoiType = ref(null); 

watch(selectedPoi, (v) => {
  if (v !== null && mapRef.value && customer.value && customer.value.pois) {
    const poi = customer.value.pois.find((el) => el.id === v);
    if (poi && poi.paths) {
      mapRef.value.fitBounds(poi.paths);
    }
  }
});

const poiList = computed(() => {
  if (customer.value === null || !customer.value.pois) return [];
  return customer.value.pois;
});

const showDropoffLocation = computed(() => {
  return currentModel.value && currentModel.value.temp && currentModel.value.temp.tripType === 'pickup';
});

const editIndex = ref(undefined);
function openAddTrip() {
  if (!currentModel.value || !currentModel.value.customerId) {
    Notify.create({
      message: 'Please select a customer first',
      classes: 'bg-red px-4',
    });
    return;
  }
  
  customer.value = props.customerList.find(
    (el) => el.id === currentModel.value.customerId,
  );
  
  if (!customer.value) {
    Notify.create({
      message: 'Customer not found',
      classes: 'bg-red px-4',
    });
    return;
  }
  
  // Initialize temp object safely
  if (!currentModel.value) {
    currentModel.value = {};
  }
  
  currentModel.value.temp = {
    customerName: customer.value.companyName,
    poiAddress: null,
    customer: customer.value,
    poi: null,
    tripType: null,
    date: null,
    time: null,
    dropoffPoi: null,
    driver: null
  };
  
  dialog.value = true;
}

function openPoiDialog(type) {
  console.log(`[Dialog] Opening POI dialog for type: ${type}`);
  poiDialog.value = true;
  selectedPoi.value = null;
  selectedPoiType.value = type;
  // Ensure map updates after dialog is rendered
  nextTick(() => {
    console.log('[Dialog] nextTick fired after opening dialog.');
    updateMapPois(); // Attempt update, the function itself will retry if map not ready
  });
}

function openDropoffPoiDialog() {
  dropoffPoiDialog.value = true;
  dropoffPoiId.value = null;
}

const mapRef = ref(null);
const visualPois = ref({});

// Function to update map POIs with robust check
function updateMapPois() {
  // Check if map object is fully ready
  if (!mapRef.value || !mapRef.value.mapRef || !mapRef.value.mapRef.$mapObject) {
    console.log('[Map Update] Map object not ready, retrying in 200ms...');
    setTimeout(updateMapPois, 200); // Retry after a delay
    return;
  }
  
  console.log('[Map Update] Map ready. Updating POIs...');
  const mapInstance = mapRef.value.mapRef.$mapObject;

  // Clear existing POIs from the map
  console.log('[Map Update] Clearing existing visual POIs:', Object.keys(visualPois.value));
  for (const id of Object.keys(visualPois.value)) {
    if (visualPois.value[id] && visualPois.value[id].setMap) {
      visualPois.value[id].setMap(null);
    }
    // Consider deleting the ref entry, but clearing the map might be enough
    // delete visualPois.value[id]; 
  }
  visualPois.value = {}; // Reset the visual POIs tracker

  // Redraw POIs if customer and POIs exist
  if (customer.value && customer.value.pois) {
    console.log(`[Map Update] Customer ${customer.value.companyName} has ${customer.value.pois.length} POIs.`);
    const allPoisPath = [];
    customer.value.pois.forEach((el) => {
      if (el.paths && window.google && window.google.maps) {
        allPoisPath.push(...el.paths);
        try {
          const poi = new google.maps.Polygon({
            paths: el.paths,
            fillColor: '#0099FF', // Default color - color logic removed for simplicity, can be added back if needed
            fillOpacity: 0.6,
            strokeColor: '#AA2143',
            strokeWeight: 2, // Slightly thinner stroke
          });
          poi.setMap(mapInstance);
          visualPois.value[el.id] = poi; // Store the new polygon
          // console.log(`[Map Update] Added polygon for POI ID: ${el.id}`);
        } catch (error) {
          console.error("[Map Update] Error rendering POI on map:", error, el);
        }
      } else {
         if (!el.paths) console.warn(`[Map Update] POI ID ${el.id} has no paths.`);
         if (!window.google || !window.google.maps) console.warn("[Map Update] Google Maps API not available while drawing.");
      }
    });

    // Fit bounds if paths exist
    if (allPoisPath.length > 0 && mapRef.value.fitBounds) {
      try {
        console.log('[Map Update] Fitting map bounds...');
        mapRef.value.fitBounds(allPoisPath);
      } catch (error) {
        console.error("[Map Update] Error fitting bounds:", error);
      }
    } else {
        console.log('[Map Update] No paths found in POIs to fit bounds.');
    }
     console.log('[Map Update] Finished updating POIs.');
  } else {
     console.log('[Map Update] No customer or POIs found to draw.');
  }
}

// onMapLoaded is triggered by the MapView component
function onMapLoaded() {
  console.log('[Map Load Event] MapView @loaded event received.');
  Notify.create({
    message: 'Map loaded.',
    classes: 'bg-green px-4',
    position: 'top',
    timeout: 1500
  });
  // Ensure updateMapPois is called, the internal check will handle timing
  updateMapPois(); 
}

// *** Ensure addSelectedPoi function is defined ***
function addSelectedPoi() {
  if (!selectedPoi.value || !customer.value || !customer.value.pois) {
    Notify.create({
      message: 'No location selected',
      classes: 'bg-red px-4',
    });
    return;
  }
  
  const selectedLocation = customer.value.pois.find(
    (el) => el.id === selectedPoi.value,
  );
  
  if (!selectedLocation) {
    Notify.create({
      message: 'Selected location not found',
      classes: 'bg-red px-4',
    });
    return;
  }
  
  if (!currentModel.value.temp) {
    currentModel.value.temp = {};
  }
  
  // Assign to the correct field based on the type selected when opening the dialog
  if (selectedPoiType.value === 'pickup') {
    currentModel.value.temp.pickupPoi = selectedLocation;
    currentModel.value.temp.pickupPoiAddress = selectedLocation.address;
    Notify.create({
      message: `${selectedLocation.name} selected as Pickup Location`,
      classes: 'bg-green px-4',
    });
  } else if (selectedPoiType.value === 'dropoff') {
    currentModel.value.temp.dropoffPoi = selectedLocation;
    currentModel.value.temp.dropoffPoiAddress = selectedLocation.address; // Make sure we have this field if needed
     Notify.create({
      message: `${selectedLocation.name} selected as Dropoff Location`,
      classes: 'bg-green px-4',
    });
  } else {
     Notify.create({
      message: 'Error: Unknown location type to assign.',
      classes: 'bg-red px-4',
    });
    console.error("Unknown selectedPoiType:", selectedPoiType.value);
  }
  
  poiDialog.value = false;
}

// *** Ensure addSelectedDropoffPoi function is defined (if still needed/used) ***
function addSelectedDropoffPoi() {
  if (!dropoffPoiId.value || !customer.value || !customer.value.pois) {
    Notify.create({
      message: 'No dropoff location selected',
      classes: 'bg-red px-4',
    });
    return;
  }
  
  const selectedLocation = customer.value.pois.find(
    (el) => el.id === dropoffPoiId.value,
  );
  
  if (!selectedLocation) {
    Notify.create({
      message: 'Selected dropoff location not found',
      classes: 'bg-red px-4',
    });
    return;
  }
  
  if (!currentModel.value.temp) {
    currentModel.value.temp = {};
  }
  
  // This function might need adjustment if only one POI dialog is used now
  currentModel.value.temp.dropoffPoi = selectedLocation;
  // Assuming we still use dropoffPoiDialog ref if this function exists
  if (typeof dropoffPoiDialog !== 'undefined') { 
      dropoffPoiDialog.value = false;
  } else {
       console.warn('addSelectedDropoffPoi called but dropoffPoiDialog ref seems undefined');
       // Close the generic poiDialog if the dropoff one isn't defined? 
       poiDialog.value = false;
  }
}

// Modify removeTrip to emit an event instead of showing dialog
function removeTrip(index) {
  console.log(`BookingAddTrip: Requesting delete for index ${index}`);
  // Basic check before emitting
  if ((currentModel.value && currentModel.value.trips) && index >= 0 && index < currentModel.value.trips.length) { 
    emit('request-delete', index); // Emit event with the index
  } else {
    console.error("removeTrip called with invalid index or missing trips array:", index);
    // Optionally notify parent of error?
    // emit('delete-error', index); 
  }
  // REMOVED Dialog.create and Notify.create logic
}

// Use defineExpose to make functions callable from parent
defineExpose({ edit, removeTrip });
</script>

<template>
  <!-- UNCOMMENT the root div -->
  <div class="flex flex-col gap-2">
    
    <!-- REMOVE QTimeline section -->
    <!-- 
    <q-timeline
      color="secondary"
      class="overflow-auto p-4"
      style="max-height: 500px"
    >
      <q-timeline-entry
        v-for="(trip, index) in currentModel && currentModel.trips || []"
        :key="index"
        draggable="true"
        class="hovercard duration-200 ease-in-out"
      >
        <template v-slot:title>
          <div class="capitalize">
            {{ trip.tripType }} - {{ trip.meta && trip.meta.customer && trip.meta.customer.companyName || 'Customer' }}
          </div>
        </template>
        <template v-slot:subtitle>
          {{ `${trip.scheduledDate || ''} - ${trip.scheduledTime || ''}` }}
        </template>
        <div class="absolute top-0 right-0">
          <q-btn
            flat
            color="primary"
            round
            icon="edit"
            @click="edit(index, trip)" 
          />
          <q-btn 
            flat 
            color="negative" 
            round 
            icon="delete" 
            @click="removeTrip(index)" 
          />
        </div>
        <div class="font-bold">
           {{ trip.locations?.find(loc => loc.primary)?.meta?.name || (trip.meta?.poi && trip.meta.poi.name) || 'Location' }}
        </div>
        <div>
           {{ trip.locations?.find(loc => loc.primary)?.meta?.address || (trip.meta?.address) || '' }}
        </div>
         <div v-if="trip.tripType === 'pickup'" class="mt-2 pt-2 border-t border-gray-300">
             <div class="font-bold text-primary">Dropoff Location:</div>
             <div class="font-medium">{{ trip.locations?.find(loc => !loc.primary)?.meta?.name || (trip.meta?.dropoffPoi && trip.meta.dropoffPoi.name) }}</div>
             <div>{{ trip.locations?.find(loc => !loc.primary)?.meta?.address || (trip.meta?.dropoffPoi && trip.meta.dropoffPoi.address) }}</div>
         </div>
         <div v-if="trip.driverId" class="mt-2 pt-2 border-t border-gray-300">
             <div class="font-bold text-primary">Driver:</div>
             <div>{{ trip.meta?.driverName || 'Assigned' }}</div>
         </div>
      </q-timeline-entry>
    </q-timeline>
    -->
    
    <q-btn color="primary" label="Add Trip" @click="openAddTrip()" class="q-mt-md"/>
    
    <q-dialog v-model="dialog" persistent>
      <q-card class="p-8 pb-2 flex flex-col gap-4" style="min-width: 500px;">
        <q-card-section>
           <div class="text-h6">{{ editIndex === undefined ? 'Add New Trip' : 'Edit Trip' }}</div>
        </q-card-section>
        <q-card-section class="flex flex-col gap-4 items-center">
          <!-- Trip type and date/time selection form -->
          <Forms :ui="form" class="w-full q-mb-md" />
          
          <!-- Driver selection -->
          <div v-if="currentModel && currentModel.temp" class="q-pa-xs q-mb-xs w-full">
            <div class="text-subtitle1 q-mb-sm">Driver Assignment</div>
            <q-select
              v-model="currentModel.temp.driver"
              :options="props.driverList || []"
              option-label="name"
              option-value="id"
              label="Select Driver"
              outlined
              dense
              clearable
              class="w-full"
            >
              <template v-slot:no-option>
                <q-item>
                  <q-item-section class="text-grey">
                    No drivers available
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
          </div>

          <!-- Dynamic Location Selection Buttons -->
          <div v-if="currentModel && currentModel.temp" class="flex flex-col gap-4 w-full">
            <!-- Pickup Location Button (shown for pickup trips) -->
            <div 
              v-if="currentModel.temp && currentModel.temp.tripType === 'pickup'" 
              class="q-pa-xs q-mb-xs w-full"
            >
              <div v-if="currentModel.temp.pickupPoi" class="q-mb-sm">
                 <div class="text-subtitle2">Pickup Location:</div>
                 <div>{{ currentModel.temp.pickupPoi.name }} ({{ currentModel.temp.pickupPoiAddress }})</div>
              </div>
              <q-btn
                color="primary"
                :label="currentModel.temp.pickupPoi ? 'Change Pickup Location' : 'Add Pickup Location'"
                @click="openPoiDialog('pickup')"
                class="w-full"
                icon="add_location"
              />
            </div>

            <!-- Dropoff Location Button (shown for pickup and dropoff trips) -->
            <div 
              v-if="currentModel.temp && currentModel.temp.tripType" 
              class="q-pa-xs q-mb-xs w-full"
            >
              <div v-if="currentModel.temp.dropoffPoi" class="q-mb-sm">
                 <div class="text-subtitle2">Dropoff Location:</div>
                 <div>{{ currentModel.temp.dropoffPoi.name }} ({{ currentModel.temp.dropoffPoiAddress }})</div>
              </div>
              <q-btn
                color="primary"
                :label="currentModel.temp.dropoffPoi ? 'Change Dropoff Location' : 'Add Dropoff Location'"
                @click="openPoiDialog('dropoff')"
                class="w-full"
                icon="add_location"
              />
            </div>
          </div>
        </q-card-section>
        
        <q-card-actions align="right" class="q-pt-md">
          <q-btn flat label="Cancel" color="red" v-close-popup />
          <q-btn
            :label="editIndex === undefined ? 'Add Trip' : 'Save Changes'"
            color="primary"
            @click="addTrip"
             :disable="!currentModel.temp || !currentModel.temp.tripType || 
                     (currentModel.temp.tripType === 'pickup' && (!currentModel.temp.pickupPoi || !currentModel.temp.dropoffPoi)) ||
                     (currentModel.temp.tripType === 'dropoff' && !currentModel.temp.dropoffPoi)"
            style="background-color: #28348a"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- POI Selection Dialog -->
    <q-dialog v-model="poiDialog" persistent>
      <q-card style="width: 90vw; max-width: 1000px;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Select {{ selectedPoiType === 'pickup' ? 'Pickup' : 'Dropoff' }} Location</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section class="row">
          <div class="col-12 col-md-8 q-pa-md" style="height: 500px;">
            <MapView class="fit" ref="mapRef" :manualPOISubmit="true" />
          </div>

          <div class="col-12 col-md-4 q-pa-md">
            <div class="text-subtitle1 q-mb-md">Available Locations</div>
            <q-list bordered separator>
              <q-item 
                v-for="poi in poiList" 
                :key="poi.id" 
                clickable 
                :active="selectedPoi === poi.id"
                @click="selectedPoi = poi.id"
                active-class="bg-primary text-white"
              >
                <q-item-section>
                  <q-item-label>{{ poi.name }}</q-item-label>
                  <q-item-label caption>{{ poi.address }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Cancel" color="primary" v-close-popup />
          <q-btn 
            label="Select" 
            color="primary" 
            @click="addSelectedPoi"
            :disable="!selectedPoi"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
    
    <!-- Dropoff POI Selection Dialog (Keep if needed for editing?) -->
    <q-dialog
      v-model="dropoffPoiDialog"
      persistent
      maximized
      transition-show="slide-up"
      transition-hide="slide-down"
    >
      <q-card class="flex flex-col">
        <q-bar>
          <q-space />
          <q-btn dense flat icon="close" v-close-popup>
            <q-tooltip content-class="bg-white text-primary">Close</q-tooltip>
          </q-btn>
        </q-bar>
        <div class="bg- flex-1 flex">
          <div class="flex-1">
            <MapView class="fit" ref="mapRef" :manualPOISubmit="true" />
          </div>
          <div
            class="flex flex-col justify-start p-4 gap-2 max-w-sm w-full"
            v-if="mapRef"
          >
            <div>Please select a Dropoff POI</div>
            <q-list bordered>
              <template v-for="(poi, index) in poiList">
                <q-separator />
                <q-item
                  clickable
                  @click="dropoffPoiId = poi.id"
                  :class="{ 'bg-blue text-white': poi.id === dropoffPoiId }"
                >
                  <q-item-section class="flex flex-col">
                    <q-item-label class="font-bold">{{
                      poi.name
                    }}</q-item-label>
                    <q-item-label
                      caption
                      lines="2"
                      class="text-xs"
                      :class="{ ' text-white': poi.id === dropoffPoiId }"
                    >
                      {{ poi.address }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </template>
            </q-list>
            <div class="flex p-4 gap-2 mt-auto">
              <q-btn
                class="w-full"
                color="primary"
                label="Add Dropoff POI"
                :disable="!dropoffPoiId"
                outlined
                dense
                @click="addSelectedDropoffPoi()"
              />
            </div>
          </div>
        </div>
      </q-card>
    </q-dialog>
  <!-- UNCOMMENT the closing root div -->
  </div> 
</template>

<style>
.hovercard:hover {
  box-shadow: 0 0 10px 2px rgba(95, 138, 158, 0.562);
  border-radius: 10px;
}
</style>
